import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { BookOpen, Plus, Trash2, Search, Sparkles, RefreshCw } from "lucide-react";

export default function KnowledgeBase() {
  const { activeCompanyId } = useAuth();
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", title: "", content: "", tags: "" });
  const [searchQ, setSearchQ] = useState("");
  const [searchCompanyId, setSearchCompanyId] = useState(""); // independent from Add-doc dialog
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [reindexing, setReindexing] = useState(false);

  const load = async () => {
    const params = activeCompanyId ? { company_id: activeCompanyId } : {};
    const [k, c] = await Promise.all([api.get("/knowledge", { params }), api.get("/companies")]);
    setItems(k.data); setCompanies(c.data);
    if (!form.company_id) setForm((f) => ({ ...f, company_id: activeCompanyId || c.data[0]?.id || "" }));
    // If the user is in a specific workspace, follow it. If they're in
    // 'All companies' mode we deliberately leave searchCompanyId empty so
    // the disabled search button + hint communicate that they must pick one.
    if (activeCompanyId) setSearchCompanyId(activeCompanyId);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCompanyId]);
  // When the workspace switches, follow it for the search scope
  useEffect(() => { if (activeCompanyId) setSearchCompanyId(activeCompanyId); }, [activeCompanyId]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/knowledge", {
        company_id: form.company_id,
        title: form.title,
        content: form.content,
        tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Knowledge added — embedding in background");
      setOpen(false);
      setForm({ ...form, title: "", content: "", tags: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete document?")) return;
    await api.delete(`/knowledge/${id}`);
    toast.success("Deleted");
    load();
  };

  const semanticSearch = async () => {
    if (!searchQ.trim() || !searchCompanyId) return;
    setSearching(true);
    try {
      const { data } = await api.post("/knowledge/search", { company_id: searchCompanyId, query: searchQ, top_k: 6 });
      setResults(data.results || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Search failed"); }
    setSearching(false);
  };

  const reindex = async () => {
    if (!searchCompanyId) return;
    setReindexing(true);
    try {
      const { data } = await api.post("/knowledge/reindex", { company_id: searchCompanyId });
      toast.success(`Re-indexed ${data.updated} docs${data.skipped ? ` (${data.skipped} skipped)` : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Reindex failed"); }
    setReindexing(false);
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto" data-testid="knowledge-page">
      <PageHeader
        title="Knowledge base"
        subtitle="Ground your AI agents in company policies, SOPs, and rate sheets. Snippets are matched to each incoming email."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-sm" onClick={reindex} disabled={reindexing || !searchCompanyId} data-testid="kb-reindex-button">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${reindexing ? "animate-spin" : ""}`} /> {reindexing ? "Re-indexing…" : "Re-index all"}
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm" data-testid="new-kb-button"><Plus className="h-4 w-4 mr-1.5" /> Add document</Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-2xl">
              <DialogHeader><DialogTitle className="font-display text-xl tracking-tight">Add knowledge document</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <F label="Company">
                  <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="kb-company-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.trading_name || c.legal_name}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
                <F label="Title"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="kb-title-input" /></F>
                <F label="Content"><Textarea rows={10} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} required data-testid="kb-content-input" /></F>
                <F label="Tags (comma separated)"><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="kb-tags-input" /></F>
                <DialogFooter><Button type="submit" className="rounded-sm" data-testid="kb-create-submit">Save</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        }
      />
      <div className="px-6 lg:px-8 pt-4">
        <div className="flex items-center gap-2 border border-border rounded-sm px-3 py-2 bg-white flex-wrap">
          <Sparkles className="h-4 w-4 text-terracotta" />
          <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Semantic search</span>
          {/* Explicit search scope — decoupled from the Add-doc dialog form so
             super admins in 'All companies' mode can pick the tenant they want. */}
          {!activeCompanyId && (
            <Select value={searchCompanyId} onValueChange={setSearchCompanyId}>
              <SelectTrigger className="h-8 w-56 rounded-sm text-xs" data-testid="kb-search-company">
                <SelectValue placeholder="Search in company…" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.trading_name || c.legal_name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Input
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && semanticSearch()}
            placeholder="How does the refund policy work?"
            className="border-0 shadow-none focus-visible:ring-0 h-8 flex-1 min-w-[200px]"
            data-testid="kb-search-input"
          />
          <Button size="sm" className="rounded-sm h-8" onClick={semanticSearch} disabled={searching || !searchQ.trim() || !searchCompanyId} data-testid="kb-search-button">
            <Search className="h-3.5 w-3.5 mr-1" /> {searching ? "Searching…" : "Search"}
          </Button>
          {results !== null && (
            <Button size="sm" variant="ghost" className="rounded-sm h-8" onClick={() => setResults(null)} data-testid="kb-search-clear">Clear</Button>
          )}
        </div>
        {!activeCompanyId && !searchCompanyId && (
          <div className="text-[11px] text-muted-foreground mt-1.5 font-mono">Pick a company to scope the search.</div>
        )}
        {results !== null && (
          <div className="mt-3 space-y-2" data-testid="kb-search-results">
            {results.length === 0 && <div className="text-xs text-muted-foreground italic">No matches. Try broader terms or re-index the KB.</div>}
            {results.map((r) => (
              <div key={r.id} className="border border-border rounded-sm p-3 bg-white" data-testid={`kb-result-${r.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium text-sm">{r.title}</div>
                  <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-brand bg-emerald-brand/10 rounded-sm px-1.5 py-0.5">score {r.score}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1 line-clamp-3 whitespace-pre-wrap">{r.snippet}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="p-6 lg:p-8 grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((k) => (
          <div key={k.id} className="border border-border rounded-sm p-4 hover:bg-muted/30 transition-colors" data-testid={`kb-${k.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-terracotta" />
                <span className="font-medium">{k.title}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => remove(k.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
            <div className="text-xs text-muted-foreground mt-2 line-clamp-4 whitespace-pre-wrap">{k.content}</div>
            <div className="mt-2 flex flex-wrap gap-1">
              {(k.tags || []).map((t) => <span key={t} className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-muted">{t}</span>)}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-muted-foreground text-sm">No knowledge documents.</div>}
      </div>
    </div>
  );
}
function F({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</Label>{children}</div>;
}
